import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { convertBoolean } from "go-core/components/Formatters/FormatBoolean";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import ReportData from "go-report/reports";
import { reportParamsConverter } from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../services/Api/Admin/adminApi";

interface State {
	resource?: Record<string, any>;
}

const listName = "ADMIN_DEVICE_REPORT";
const resourceType = "DEVICE_REPORT";

const Report = ({ resource }: State) => {
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const configId = "admin_devices_report_report";
	let config = {
		reportConfigId: configId,
		aggregatePrefix: "summary",
		selectedColumns: ["count"],
		groupsInfo: {
			STATUS: (groupByValue: Record<string, any>) => {
				return t(`lib:enums.common.status.${groupByValue.name}`);
			},
			MASTER: (groupByValue: Record<string, any>) => {
				return convertBoolean(groupByValue.name === "true", t);
			},
			TYPE: (groupByValue: Record<string, any>) => {
				return t(`enums.device.type.${groupByValue.name}`);
			},
		},
		groups: [
			{
				id: "NAME",
				name: t("lib:common.word.name"),
			},
			{
				id: "CREATED_AT",
				name: t("lib:common.word.created_at"),
			},
			{
				id: "UPDATED_AT",
				name: t("modules.device_report.field.updated_at.title"),
			},
			{
				id: "CONNECTION_INTERFACE",
				name: t("modules.device_report.field.connection_interface.title"),
			},
			{
				id: "TYPE",
				name: t("lib:common.word.type"),
			},
			{
				id: "DRIVER",
				name: t("modules.device_report.field.driver.title"),
			},
			{
				id: "STATUS",
				name: t("lib:common.word.status"),
			},
			{
				id: "ORGANIZATION",
				name: t("lib:common.word.location"),
			},
		],
		showPercent: true,
		columns: [
			{
				id: "count",
				name: t("common.word.amount"),
			},
		],
		selectedGroups: ["NONE"],
		filters: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
			},
			{
				id: "updated_at",
				name: t("modules.device_report.field.updated_at.title"),
				type: "date",
			},
			{
				id: "driver",
				name: t("modules.device_report.field.driver.title"),
				type: "text",
			},
			{
				id: "type",
				name: t("lib:common.word.type"),
				type: "list",
				listOptions: {
					PRINTER: t("enums.device.type.PRINTER"),
					PAYMENT_TERMINAL: t("enums.device.type.PAYMENT_TERMINAL"),
					WEIGHT: t("enums.device.type.WEIGHT"),
					CODE_READER: t("enums.device.type.CODE_READER"),
					UNKNOWN: t("enums.device.type.UNKNOWN"),
				},
			},
			{
				id: "connection_interface",
				name: t("modules.device_report.field.connection_interface.title"),
				type: "list",
				listOptions: {
					USB: "USB",
					MAC: "MAC",
					LAN: "LAN",
					BLUETOOTH: "BLUETOOTH",
					NEXT: "NEXT",
					INTERNAL: "INTERNAL",
					COM: "COM",
				},
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				listOptions: {
					ENABLED: t("enums.common.status.ENABLED", { ns: "lib" }),
					DISABLED: t("enums.common.status.DISABLED", { ns: "lib" }),
					DELETED: t("enums.common.status.DELETED", { ns: "lib" }),
				},
			},
			{
				id: "organization",
				name: t("lib:common.word.location"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminOrganizationsListSearchSelect(search, params, undefined, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "count",
				name: t("common.word.amount"),
				type: "number",
			},
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		segments: [
			{
				id: "all",
				name: `${t("common.word.all", { ns: "lib" })}`,
				slug: "all",
				columns: ["count"],
				filters: [],
				groups: [],
			},
		],
		fetch: async (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return Promise.all([
				adminApi.getAdminDevicesReport(reportParamsConverter(params), { cancelToken: sourceToken?.token }),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.device_report.header.title"),
			filename: t("modules.device_report.field.export_config.filename"),
		},
		doNotUseDefaultDateRangeInFilters: true,
	} as ReportConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const AdminDeviceReportPage = () => {
	const [resource, setResource] = useState<any>();
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.device_report.header.title")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className={"content"}>
			{!isMobile && <Header title={t("modules.device_report.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>{<Report resource={resource} />}</Suspense>
		</div>
	);
};

export default AdminDeviceReportPage;
